import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ViewChildren,
  QueryList,
  ElementRef,
  AfterViewInit,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-otp-input',
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OtpInputComponent),
      multi: true,
    },
  ],
})
export class OtpInputComponent
  implements ControlValueAccessor, OnInit, AfterViewInit
{
  @Input() length: number = 6;
  @Input() inputType: 'text' | 'password' | 'tel' = 'password';
  @Input() hasError: boolean = false;
  @Input() autoFocus: boolean = true;
  @Output() complete = new EventEmitter<string>();
  @Output() enterPressed = new EventEmitter<void>();

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  otpValues: string[] = [];
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit() {
    this.otpValues = Array(this.length).fill('');
  }

  ngAfterViewInit() {
    if (this.autoFocus && this.otpInputs.first) {
      setTimeout(() => this.otpInputs.first.nativeElement.focus(), 0);
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  onInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Only allow single digit
    if (value.length > 1) {
      input.value = value.charAt(value.length - 1);
    }

    this.otpValues[index] = input.value;
    this.updateValue();

    // Move to next input if value entered
    if (input.value && index < this.length - 1) {
      const nextInput = this.otpInputs.toArray()[index + 1];
      if (nextInput) {
        nextInput.nativeElement.focus();
      }
    }

    // Emit complete event if all fields filled
    if (this.otpValues.every((v) => v !== '')) {
      this.complete.emit(this.otpValues.join(''));
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;

    // Handle backspace
    if (event.key === 'Backspace' && !input.value && index > 0) {
      const prevInput = this.otpInputs.toArray()[index - 1];
      if (prevInput) {
        prevInput.nativeElement.focus();
        prevInput.nativeElement.select();
      }
    }

    // Handle enter
    if (event.key === 'Enter' && this.otpValues.every((v) => v !== '')) {
      this.enterPressed.emit();
    }

    // Only allow numbers
    if (event.key.length === 1 && !/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent, index: number) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.replace(/\D/g, '').split('');

    const inputs = this.otpInputs.toArray();
    digits.forEach((digit, i) => {
      const targetIndex = index + i;
      if (targetIndex < this.length) {
        inputs[targetIndex].nativeElement.value = digit;
        this.otpValues[targetIndex] = digit;
      }
    });

    this.updateValue();

    // Focus next empty or last filled
    const nextIndex = Math.min(index + digits.length, this.length - 1);
    inputs[nextIndex].nativeElement.focus();

    // Emit complete if all filled
    if (this.otpValues.every((v) => v !== '')) {
      this.complete.emit(this.otpValues.join(''));
    }
  }

  private updateValue() {
    const value = this.otpValues.join('');
    this.onChange(value);
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    if (value) {
      const digits = value.split('').slice(0, this.length);
      this.otpValues = [
        ...digits,
        ...Array(this.length - digits.length).fill(''),
      ];

      if (this.otpInputs) {
        const inputs = this.otpInputs.toArray();
        this.otpValues.forEach((val, i) => {
          if (inputs[i]) {
            inputs[i].nativeElement.value = val;
          }
        });
      }
    } else {
      this.otpValues = Array(this.length).fill('');
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (this.otpInputs) {
      this.otpInputs.forEach((input) => {
        input.nativeElement.disabled = isDisabled;
      });
    }
  }

  clear() {
    this.otpValues = Array(this.length).fill('');
    if (this.otpInputs) {
      this.otpInputs.forEach((input) => {
        input.nativeElement.value = '';
      });
      if (this.otpInputs.first) {
        this.otpInputs.first.nativeElement.focus();
      }
    }
    this.updateValue();
  }
}
